import { Decimal } from "decimal.js";

import type { FuelPaymentCreate } from "@/types/fuelPayment";

export const MAX_FUEL_BALANCE = "99999999.99";

const MAX_PAYMENT_AMOUNT = new Decimal(MAX_FUEL_BALANCE);
const PAYMENT_AMOUNT_PATTERN = /^\d+(?:\.\d{1,2})?$/;
const FUEL_BALANCE_STEP = new Decimal("0.01");

type FuelBalanceStepDirection = "increase" | "decrease";

export interface BuildFuelPaymentPayloadInput {
  userId: number;
  amountUsdt: string;
  comment?: string;
}

export const normalizeFuelPaymentAmount = (value: string): string | null => {
  const normalized = value.trim().replace(",", ".");
  if (!PAYMENT_AMOUNT_PATTERN.test(normalized)) return null;

  const amount = new Decimal(normalized);
  if (!amount.greaterThan(0) || amount.greaterThan(MAX_PAYMENT_AMOUNT)) return null;

  return amount.toFixed(2);
};

export const normalizeFuelBalance = (value: string): string | null => {
  const normalized = value.trim().replace(",", ".");
  if (!PAYMENT_AMOUNT_PATTERN.test(normalized)) return null;

  const balance = new Decimal(normalized);
  if (balance.isNegative() || balance.greaterThan(MAX_PAYMENT_AMOUNT)) return null;

  return balance.toFixed(2);
};

export const stepFuelBalance = (value: string, direction: FuelBalanceStepDirection): string => {
  const normalizedBalance = normalizeFuelBalance(value);
  const currentBalance = new Decimal(normalizedBalance ?? 0);
  const nextBalance =
    direction === "increase"
      ? currentBalance.plus(FUEL_BALANCE_STEP)
      : currentBalance.minus(FUEL_BALANCE_STEP);

  if (nextBalance.isNegative()) return "0.00";
  if (nextBalance.greaterThan(MAX_PAYMENT_AMOUNT)) return MAX_PAYMENT_AMOUNT.toFixed(2);

  return nextBalance.toFixed(2);
};

export const buildFuelPaymentPayload = (
  input: BuildFuelPaymentPayloadInput,
  idFactory: () => string = () => crypto.randomUUID()
): FuelPaymentCreate => ({
  user_id: input.userId,
  amount_usdt: input.amountUsdt,
  idempotency_key: idFactory(),
  comment: input.comment,
});
